import { produce } from 'immer';

import PENDING_TYPE from '@app/constants/pendingType';
import {
  SIGNING_PROGRESS_INFORMATION__FETCH_REQUEST,
  SIGNING_PROGRESS_INFORMATION__FETCH_SUCCESS,
} from '@app/store/actionTypes';
import Status from '@app/store/document/Status';
import { HostReference } from '@app/store/Host';
import { SigningProgressInformationAction } from '@app/store/signingProgressInformation/actions';

interface SigningProgressInformationParticipant {
  digitalCertificateAuth: { bizNumber: string; companyName: string } | null;
  isPasswordAuth: boolean;
  mobileAuth: {
    phoneNumber: string;
    realName: string;
  } | null;
  name: string;
  participantId: string;
  progressStatus: string;
  signingExpiryDate: string | null;
  signingMethod: {
    contactInfo: string;
    methodType: string;
  };
  signingOrder: number;
  host?: HostReference;
}

export type SigningProgressInformationParticipantsState = SigningProgressInformationParticipant[];

const initialState: SigningProgressInformationParticipantsState = [];

const SCHEDULE_OR_PENDING_STATUS = [
  Status.PENDING,
  Status.SCHEDULE_READY,
  Status.SCHEDULE_CANCELED,
  Status.SCHEDULE_START_FAILED,
];

export default function reducer(
  state: SigningProgressInformationParticipantsState = initialState,
  action: SigningProgressInformationAction
) {
  switch (action.type) {
    case SIGNING_PROGRESS_INFORMATION__FETCH_REQUEST:
      return initialState;
    case SIGNING_PROGRESS_INFORMATION__FETCH_SUCCESS:
      return produce(state, (draft) => {
        if (action.payload) {
          const {
            participantAuths,
            document: { participants, signings, pending, status, currentSigningOrder },
          } = action.payload;
          const pendingParticipantId = pending && pending.participantId;
          const pendingType = pending && pending.pendingType;

          participants.forEach((participant) => {
            const { participantId } = participant;

            const isPendingCreator = pendingParticipantId === participantId;
            const isSigningDone = !!signings
              .filter((signing) => !signing.expiredAt)
              .find((signing) => signing.participantId === participantId);
            const signingExpiryDate = participant.signingExpiryDate.date;
            const isExpiryVaild = participant.signingExpiryDate.isValid;

            let progressStatus = '';

            // 서명을 완료한 서명자
            if (isSigningDone) {
              progressStatus = '서명 완료';

              // 거절한 서명자
            } else if (isPendingCreator && pendingType === PENDING_TYPE.REJECTION) {
              progressStatus = '서명 거절';

              // 서명 취소한 서명자
            } else if (isPendingCreator && pendingType === PENDING_TYPE.SIGNING_CANCELLATION) {
              progressStatus = '서명 취소';

              // 예약 상태이거나 대기중에 중단당한 사용자
            } else if (SCHEDULE_OR_PENDING_STATUS.includes(status) && !isSigningDone) {
              progressStatus = '-';

              // 서명 차례인 서명자
            } else if (currentSigningOrder === participant.signingOrder && isExpiryVaild) {
              progressStatus = '서명 필요';

              // 서명 차례에 유효기간이 만료된 서명자
            } else if (currentSigningOrder === participant.signingOrder && !isExpiryVaild) {
              progressStatus = '서명 유효기간 만료';

              // 서명 차례를 기다리는 서명자
            } else if (!isSigningDone) {
              progressStatus = '대기 중';
            }

            const participantAuth = participantAuths.find(
              (participantAuthValue) => participantAuthValue.participantId === participantId
            );

            draft.push({
              ...participant,
              digitalCertificateAuth: participantAuth?.digitalCertificate ?? null,
              isPasswordAuth: participantAuth?.password ?? false,
              mobileAuth: participantAuth?.mobile ?? null,
              progressStatus,
              signingExpiryDate,
            });
          });
        }
      });
    default:
      return state;
  }
}
